import React, { useState, useEffect } from 'react';
import { cmsApi } from '../../../lib/api/cms';
import TranslationEditor from '../TranslationEditor';
import ImageUpload from '../ImageUpload';
import MediaUpload from '../MediaUpload';

interface Service {
  id: string;
  title: string;
  short_description: string;
  full_description: string;
  image_url: string;
  order_index: number;
  media?: MediaItem[];
}

interface MediaItem {
  id: string;
  url: string;
  altText: string;
  caption?: string;
  order: number;
}

const ServicesEditor: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default services data from the existing website
  const defaultServices = [
    { id: 'raieoiguse-ost', title: 'Raieõiguse ost' },
    { id: 'metsamaterjali-ost', title: 'Metsamaterjali ost' },
    { id: 'hakkepuidu-ost-muuk', title: 'Hakkepuidu ost ja müük' },
    { id: 'kuttepuude-muuk', title: 'Küttepuude müük' },
    { id: 'metsamaa-ost-muuk', title: 'Metsamaa ost ja müük' },
    { id: 'metsamaterjali-ulestootamine', title: 'Metsamaterjali ülestöötamine' },
    { id: 'metsa-istutamine', title: 'Metsa istutamine' },
    { id: 'metsamaterjali-transport', title: 'Metsamaterjali transport' }
  ];

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const { data, error } = await cmsApi.getServices();
      if (error) {
        setError('Teenuste laadimine ebaõnnestus');
        return;
      }
      
      // If no services in database, create default ones
      if (!data || data.length === 0) {
        await initializeDefaultServices();
        return;
      }
      
      // Load media for each service
      const servicesWithMedia = await Promise.all(
        data.map(async (service) => {
          const media = await loadServiceMedia(service.id);
          return { ...service, media };
        })
      );
      
      setServices(servicesWithMedia || []);
      if (servicesWithMedia && servicesWithMedia.length > 0 && activeTab === 'overview') {
        setActiveTab(servicesWithMedia[0].id);
      }
    } catch (err) {
      setError('Teenuste laadimine ebaõnnestus');
    } finally {
      setLoading(false);
    }
  };

  const loadServiceMedia = async (serviceId: string): Promise<MediaItem[]> => {
    try {
      // Try to get media from settings or a separate media table
      const { data } = await cmsApi.getSetting(`service_media_${serviceId}`);
      if (data?.value) {
        return JSON.parse(data.value);
      }
      return [];
    } catch (err) {
      console.warn(`Failed to load media for service ${serviceId}:`, err);
      return [];
    }
  };

  const saveServiceMedia = async (serviceId: string, media: MediaItem[]) => {
    try {
      await cmsApi.upsertSetting(`service_media_${serviceId}`, JSON.stringify(media));
      
      // Update local state
      setServices(prev => prev.map(service => 
        service.id === serviceId ? { ...service, media } : service
      ));
    } catch (err) {
      setError('Meedia salvestamine ebaõnnestus');
    }
  };

  const initializeDefaultServices = async () => {
    try {
      const servicePromises = defaultServices.map((service, index) => 
        cmsApi.upsertService({
          id: service.id,
          title: service.title,
          short_description: '',
          full_description: '',
          image_url: '',
          order_index: index
        })
      );

      await Promise.all(servicePromises);
      await loadServices(); // Reload after initialization
    } catch (err) {
      console.error('Failed to initialize default services:', err);
    }
  };

  const handleServiceImageChange = async (serviceId: string, imageUrl: string | null) => {
    try {
      const service = services.find(s => s.id === serviceId);
      if (!service) return;

      // Exclude media property from service object before upserting
      const { media, ...serviceWithoutMedia } = service;
      
      const { error } = await cmsApi.upsertService({
        ...serviceWithoutMedia,
        image_url: imageUrl || ''
      });

      if (error) {
        setError('Pildi salvestamine ebaõnnestus');
        return;
      }

      setServices(prev => prev.map(s => 
        s.id === serviceId ? { ...s, image_url: imageUrl || '' } : s
      ));

      // Trigger website update to refresh service images on frontend
      window.dispatchEvent(new CustomEvent('translationsUpdated'));

      // Show success message
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
      successDiv.textContent = 'Teenuse pilt edukalt salvestatud!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);

    } catch (err) {
      setError('Pildi salvestamine ebaõnnestus');
    }
  };

  const handleServiceMediaChange = async (serviceId: string, media: MediaItem[]) => {
    await saveServiceMedia(serviceId, media);
  };

  const getServiceTranslationFields = (serviceId: string) => [
    {
      key: `services.items.${serviceId}.title`,
      label: 'Teenuse pealkiri',
      type: 'text' as const,
      placeholder: 'Sisesta teenuse pealkiri'
    },
    {
      key: `services.items.${serviceId}.description`,
      label: 'Lühikirjeldus',
      type: 'textarea' as const,
      placeholder: 'Sisesta teenuse lühikirjeldus (kuvatakse teenuste kaardil)'
    },
    {
      key: `services.items.${serviceId}.fullDescription`,
      label: 'Täielik kirjeldus',
      type: 'textarea' as const,
      placeholder: 'Sisesta teenuse täielik kirjeldus (kuvatakse detailvaates)'
    }
  ];

  const sectionTranslationFields = [
    {
      key: 'services.title',
      label: 'Sektsiooni pealkiri',
      type: 'text' as const,
      placeholder: 'Meie teenused'
    },
    {
      key: 'services.subtitle',
      label: 'Sektsiooni alampealkiri',
      type: 'textarea' as const,
      placeholder: 'Pakume mitmekülgseid metsandusega seotud teenuseid...'
    },
    {
      key: 'services.readMore',
      label: 'Loe rohkem tekst',
      type: 'text' as const,
      placeholder: 'Loe rohkem'
    },
    {
      key: 'services.contactUs',
      label: 'Kontakti nupu tekst',
      type: 'text' as const,
      placeholder: 'Võta ühendust'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Teenuste haldus</h1>
        <p className="mt-2 text-gray-600">
          Halda teenuste sisu, pilte ja kirjeldusi. Muudatused kajastuvad kohe veebilehel.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Ülevaade
            </button>
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => setActiveTab(service.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === service.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {service.title || 'Teenus'}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' ? (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Teenuste sektsiooni sisu</h3>
              <p className="text-sm text-gray-500 mb-6">
                Need tekstid kuvatakse teenuste sektsiooni päises
              </p>
              <TranslationEditor
                section="services"
                fields={sectionTranslationFields}
                onSave={() => {
                  window.dispatchEvent(new CustomEvent('translationsUpdated'));
                }}
              />
            </div>
          ) : (
            <div>
              {(() => {
                const service = services.find(s => s.id === activeTab);
                if (!service) return null;

                return (
                  <div className="space-y-8">
                    {/* Service Content */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        {service.title} - Sisu
                      </h3>
                      <p className="text-sm text-gray-500 mb-6">
                        Halda selle teenuse tekste ja kirjeldusi
                      </p>
                      <TranslationEditor
                        section={`service-${service.id}`}
                        fields={getServiceTranslationFields(service.id)}
                        onSave={() => {
                          window.dispatchEvent(new CustomEvent('translationsUpdated'));
                        }}
                      />
                    </div>

                    {/* Main Service Image */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Peamine teenuse pilt</h4>
                      <p className="text-sm text-gray-500 mb-4">
                        See pilt kuvatakse teenuse kaardil avalehel ja detailvaate päises
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-blue-700">
                          💡 <strong>Oluline:</strong> Siin üles laetud pilt kuvatakse automaatselt veebilehe avalehel vastava teenuse kaardil. 
                          Muudatused kajastuvad kohe pärast salvestamist.
                        </p>
                      </div>
                      <ImageUpload
                        currentImage={service.image_url}
                        onImageChange={(imageUrl) => handleServiceImageChange(service.id, imageUrl)}
                        folder="services"
                        label="Teenuse peamine pilt"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        Soovitatud suurus: 800x600px. Maksimaalne failisuurus: 5MB.
                      </p>
                    </div>

                    {/* Media Gallery */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Teenuse pildigalerii</h4>
                      <p className="text-sm text-gray-500 mb-4">
                        Kuni 6 kvaliteetset pilti, mis kuvatakse teenuse detailvaates. 
                        Esimene pilt on kõige olulisem ja kuvatakse esikohal.
                      </p>
                      <MediaUpload
                        serviceId={service.id}
                        currentMedia={service.media || []}
                        onMediaChange={(media) => handleServiceMediaChange(service.id, media)}
                        maxImages={6}
                        className="border border-gray-200 rounded-lg p-4"
                      />
                    </div>

                    {/* Media Usage Guidelines */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h5 className="font-medium text-amber-800 mb-2">📸 Piltide kasutamise juhised</h5>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• <strong>Peamine pilt:</strong> Kuvatakse avalehe teenuse kaardil</li>
                        <li>• <strong>Galerii pildid:</strong> Kuvatakse ainult teenuse detailvaates</li>
                        <li>• <strong>Kvaliteet:</strong> Kasutage kõrge kvaliteediga pilte (min 800x600px)</li>
                        <li>• <strong>Järjestus:</strong> Esimene galerii pilt on kõige olulisem</li>
                        <li>• <strong>Alt tekst:</strong> Kirjeldage iga pilti ligipääsetavuse jaoks</li>
                        <li>• <strong>Sisu:</strong> Pildid peaksid olema seotud konkreetse teenusega</li>
                      </ul>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicesEditor;